/**
 * SOLID Review
 * S — Strong. One concern: the object behind the frontend Domain's lollipop — every op answered by the context
 *     service that owns it (TablePlay, TableSync, Session, Seating, Legal, Media, Payment, Ads, Diagnostics).
 * O — Strong. A new decision is a row in the contract and a row in its service's table; nothing here changes.
 * L — Strong. Implements `IPortClientDecisions` exactly; every op is built async like the port.
 * I — Strong. Exactly the port's surface.
 * D — Strong. The services `ComponentFrontendDomain` builds and hands it, seen through their decision tables; the
 *     port's type and contract. No port of its own, no I/O.
 */
import { ClientDecisionsContract, type IPortClientDecisions } from '../ports/ClientDecisions.contract';
import type { IContextRequest } from '../ports/schemas/ContextRequest';
import type { IConfigDepClientServices } from './ClientServices';
import type { TClientOp, TDecisionRow, TDecisionRows } from './IServiceDecisionTable';

/**
 * ASYNC IN SIGNATURE, PURE IN FACT (as the backend's `DomainDecisions`). Every op returns a Promise because the port
 * is declared async — uniform with every other non-local port, so the lollipop can be remoted — but none of them
 * awaits anything: a decision is a function of its arguments. `ctx` is accepted and unread; the Domain does not know
 * what a request is. Unlike the backend's, there is no environment to bind: a client decision reads no clock and
 * mints no id.
 *
 * A DISPATCHER SINCE FRONTEND-DOMAIN-OO P5 (2026-10-26; handbook §28.272) — the backend's Facade since application-oo
 * P5 (§28.232). Until then this class held one method per op, each forwarding to a decide function; a new decision
 * was a method here too. Each context service now offers its decisions as a table (`IServiceDecisionTable`), and the
 * constructor merges them and builds every op of the contract once, from its row. tsc refuses a merge that leaves an
 * op out (it is typed over the whole port); the constructor refuses an op that no service, or two, answer for. The
 * interface merged into this class below is what the constructor guarantees: every op of `IPortClientDecisions`, as
 * an own method.
 */
export interface ClientDecisions extends IPortClientDecisions {}
export class ClientDecisions {
  constructor(services: IConfigDepClientServices) {
    const rows = ClientDecisions.merged(services);
    for (const op of ClientDecisions.opsOf(rows)) Object.assign(this, { [op]: ClientDecisions.answering(rows, op) });
  }

  /** Op `K` on the port: its row, made async as the port is, the request unread. */
  private static answering<K extends TClientOp>(rows: TDecisionRows<TClientOp>, op: K) {
    const row: TDecisionRow<K> = rows[op];
    return async (_ctx: IContextRequest, input: Parameters<TDecisionRow<K>>[0], command: Parameters<TDecisionRow<K>>[1]) => row(input, command);
  }

  /** The services' tables as one, after checking that every op of the contract has exactly one service answering it. */
  private static merged(services: IConfigDepClientServices): TDecisionRows<TClientOp> {
    const providers = new Map<string, number>();
    for (const table of services.tables()) {
      for (const op of Object.keys(table)) providers.set(op, (providers.get(op) ?? 0) + 1);
    }
    for (const op of ClientDecisionsContract.ops) {
      const count = providers.get(op) ?? 0;
      if (count !== 1) throw new Error(`ClientDecisions: ${count} context services answer '${op}'; exactly one must.`);
    }
    const rows: TDecisionRows<TClientOp> = {
      ...services.play.decisions, ...services.sync.decisions, ...services.session.decisions, ...services.seating.decisions,
      ...services.legal.decisions, ...services.media.decisions, ...services.payment.decisions, ...services.ads.decisions,
      ...services.diagnostics.decisions,
    };
    return rows;
  }

  /** The ops a complete table holds: every key of the port, which is what `TDecisionRows<TClientOp>` types it as. */
  private static opsOf(rows: TDecisionRows<TClientOp>): readonly TClientOp[] {
    return Object.keys(rows) as TClientOp[];
  }
}
